import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FORM_ERROR } from 'final-form';
import arrayMutators from 'final-form-arrays';

import { useSelector } from 'hooks';
import AdviceStatus from 'types/AdviceStatus';
import FormKeys from 'modules/form/types/FormKeys';
import { isModalOpen } from 'modules/basket/selectors';
import {
  getSelectedSelfService,
  isRequiredConsentOnWithdrawalRights as isRequiredConsentOnWithdrawalRightsSelector,
} from 'modules/selfServices/selectors';

import type PageBaseProps from 'types/PageBase';
import type { FormValidator } from 'types/Form';
import { PoliciesForm } from 'types/PoliciesForm';
import { RepairOrderForm } from 'types/RepairOrderForm';
import type { ExtrasForm as ExtrasFormType } from 'types/ExtrasForm';

import Step from './components/Step';
import Modal from './components/Modal';
import ConnectedForm from '../ui/ConnectedForm';
import PopupErrorForm from '../Errors/PopupErrorForm';

const FORM_KEYS = [FormKeys.EXTRAS, FormKeys.REPAIR_ORDER, FormKeys.POLICIES];

const Basket: React.FC<PageBaseProps> = ({
  onNext,
  onPrev,
  shouldDisplayBackButton,
}) => {
  const isBasketModalOpen = useSelector(isModalOpen);
  const selfService = useSelector(getSelectedSelfService);
  const isRequiredConsentOnWithdrawalRights = useSelector(isRequiredConsentOnWithdrawalRightsSelector);

  const [key, setKey] = React.useState(uuidv4());

  const validateForm: FormValidator<{
    [FormKeys.EXTRAS]: ExtrasFormType,
    [FormKeys.REPAIR_ORDER]: RepairOrderForm,
    [FormKeys.POLICIES]: PoliciesForm,
  }> = React.useCallback(({ extras = {}, repairOrder = {}, policies = {} }) => {
    const { extras: extraList = [] } = extras;
    const { proactiveWorks: proactiveWorksList = [] } = repairOrder;

    const hasAcceptedExtras = extraList.some(({ status }) => status === AdviceStatus.ACCEPTED);
    const hasAcceptedProactiveWork = proactiveWorksList.some(({ status }) => status === AdviceStatus.ACCEPTED);
    const hasRepairOrder = selfService.repairOrder?.length > 0;

    const hasConsent = policies.basketGTC && (!isRequiredConsentOnWithdrawalRights || policies.basketConsent);

    const hasError = !hasConsent && (hasAcceptedExtras || hasAcceptedProactiveWork || hasRepairOrder);

    return ({
      [FORM_ERROR]: hasError ? 'required' : undefined,
    });
  }, [selfService.repairOrder?.length, isRequiredConsentOnWithdrawalRights]);

  const Component = React.useMemo(
    () => (typeof onNext === 'function' ? Step : Modal),
    [onNext],
  );

  // Use to refresh the form when it has been updated in the basket modal.
  React.useEffect(() => {
    if (!isBasketModalOpen) {
      setKey(uuidv4());
    }
  }, [isBasketModalOpen]);

  return (
    <React.Fragment key={key}>
      <ConnectedForm
        onPrev={onPrev}
        onSubmit={onNext}
        component={Component}
        validate={validateForm}
        formKeys={FORM_KEYS}
        mutators={{ ...arrayMutators }}
        subscription={{ hasValidationErrors: true }}
        shouldDisplayBackButton={shouldDisplayBackButton}
        errorComponent={<PopupErrorForm errorMessage={{
          id: 'basket.mandatoryModal.title',
          defaultMessage: 'You must first consent and accept the general terms and conditions',
        }}/> }
      />
    </React.Fragment>
  );
};

export default Basket;
